import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { breakdownsAPI, vehiclesAPI, trailersAPI, driversAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  Plus, 
  Loader2, 
  Calendar,
  Truck,
  CheckCircle2,
  Clock,
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  Send,
  Check,
  X,
  Upload,
  Download,
  FileText,
  User,
  CloudRain,
  Sun,
  Moon,
  XCircle,
  File,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTING_DOC_TYPES = {
  driver_statement: 'Driver Statement',
  maintenance_history: 'Maintenance History',
  last_service: 'Last Service',
  last_inspection_sheet: 'Last Inspection Sheet',
  workshop_findings: 'Workshop Findings',
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700',
    submitted: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    returned: 'bg-red-100 text-red-700',
    closed: 'bg-slate-200 text-slate-800',
  };
  return styles[status] || 'bg-slate-100 text-slate-700';
};

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    returned: 'Returned',
    closed: 'Closed',
  };
  return labels[status] || status;
};

// Supporting Document Upload Component
const SupportingDocUpload = ({ docKey, label, docData, onUpload, onReasonChange, breakdownId, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const hasFile = docData?.uploaded;
  const filename = docData?.filename;
  const reason = docData?.reason || '';

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!breakdownId) {
      toast.error('Please save the breakdown first before uploading documents');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docKey);
      
      const uploadResp = await axios.post(
        `${API_URL}/api/breakdowns/${breakdownId}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = uploadResp.data;
      onUpload(docKey, result.filename);
      toast.success(`${label} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${label}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasFile ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-slate-300" />
          )}
          <span className={`text-sm font-medium ${hasFile ? 'text-emerald-700' : 'text-slate-600'}`}>
            {label}
          </span>
        </div>
        <label>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            disabled={disabled || uploading}
          />
          <Button
            variant={hasFile ? "outline" : "default"}
            size="sm"
            className="gap-1"
            disabled={disabled || uploading}
            asChild
          >
            <span>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {hasFile ? 'Replace' : 'Upload'}
            </span>
          </Button>
        </label>
      </div>
      
      {hasFile && filename && (
        <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded px-2 py-1">
          <File className="w-4 h-4" />
          <span className="truncate flex-1">{filename}</span>
          <a 
            href={`${API_URL}/api/uploads/breakdowns/${filename}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline flex items-center gap-1 text-blue-600"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
      
      {!hasFile && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Reason if not uploaded *</Label>
          <Input
            value={reason}
            onChange={(e) => onReasonChange(docKey, e.target.value)}
            placeholder="Provide reason for not uploading this document"
            className="text-sm"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

// Breakdown Detail Dialog
const BreakdownDetailDialog = ({ open, onClose, breakdown, onApprove, onReturn, canApprove }) => {
  if (!breakdown) return null;

  const supportingDocs = breakdown.supporting_docs || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Breakdown Report Details
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={getStatusBadge(breakdown.status)}>
              {getStatusLabel(breakdown.status)}
            </Badge>
            <span className="text-sm text-slate-500">
              {breakdown.date_of_breakdown?.slice(0, 10)}
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs text-slate-500">Truck</Label>
              <p className="font-mono font-bold">{breakdown.truck_registration}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Trailer</Label>
              <p className="font-mono">{breakdown.trailer_registration || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Driver</Label>
              <p>{breakdown.driver_name || 'Unknown'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date/Time</Label>
              <p>{breakdown.date_of_breakdown?.replace('T', ' ')}</p>
            </div>
          </div>

          {/* Supporting Documents */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Supporting Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => {
                const doc = supportingDocs[key] || {};
                // Handle both old boolean format and new object format
                const isUploaded = typeof doc === 'boolean' ? doc : doc.uploaded;
                const filename = typeof doc === 'object' ? doc.filename : null;
                const reason = typeof doc === 'object' ? doc.reason : null;
                
                return (
                  <div key={key} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-2">
                      {isUploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                      <span className="text-sm">{label}</span>
                    </div>
                    {isUploaded && filename ? (
                      <a 
                        href={`${API_URL}/api/uploads/breakdowns/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    ) : isUploaded ? (
                      <span className="text-xs text-emerald-600">Attached (legacy)</span>
                    ) : (
                      <span className="text-xs text-slate-500 italic max-w-[200px] truncate">
                        {reason || 'Not uploaded'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Uploaded Documents List */}
          {breakdown.uploaded_documents && breakdown.uploaded_documents.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
                <File className="w-4 h-4" />
                Uploaded Files ({breakdown.uploaded_documents.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {breakdown.uploaded_documents.map((filename, idx) => (
                  <a
                    key={idx}
                    href={`${API_URL}/api/uploads/breakdowns/${filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 transition-colors"
                  >
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-600 truncate flex-1">{filename}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Conditions */}
          {breakdown.conditions && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Conditions of Event</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Day/Night</Label>
                  <p className="capitalize">{breakdown.conditions.day_night || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Weather</Label>
                  <p className="capitalize">{breakdown.conditions.weather || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Time</Label>
                  <p>{breakdown.conditions.time_of_event || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Day of Week</Label>
                  <p>{breakdown.conditions.day_of_week || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Company Rep & Reporting */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Company Representative & Reporting</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 border rounded">
                <Label className="text-xs text-slate-500">Company rep at scene?</Label>
                <p className={breakdown.company_rep_present?.answer ? 'text-emerald-600' : 'text-red-600'}>
                  {breakdown.company_rep_present?.answer ? 'Yes' : 'No'}
                </p>
                {!breakdown.company_rep_present?.answer && breakdown.company_rep_present?.reason && (
                  <p className="text-sm text-slate-500 mt-1">Reason: {breakdown.company_rep_present.reason}</p>
                )}
              </div>
              <div className="p-3 border rounded">
                <Label className="text-xs text-slate-500">Driver reported immediately?</Label>
                <p className={breakdown.driver_reported_immediately?.answer ? 'text-emerald-600' : 'text-red-600'}>
                  {breakdown.driver_reported_immediately?.answer ? 'Yes' : 'No'}
                </p>
                {!breakdown.driver_reported_immediately?.answer && breakdown.driver_reported_immediately?.reason && (
                  <p className="text-sm text-slate-500 mt-1">Reason: {breakdown.driver_reported_immediately.reason}</p>
                )}
              </div>
            </div>
          </div>

          {/* Root Cause Analysis */}
          {breakdown.root_cause_analysis && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Root Cause Analysis</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Problem Description</Label>
                  <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.problem_description || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">6 WHY's Analysis</Label>
                  <div className="space-y-1 bg-slate-50 p-2 rounded">
                    {[1,2,3,4,5,6].map(num => (
                      breakdown.root_cause_analysis[`why_${num}`] && (
                        <p key={num} className="text-sm">
                          <span className="font-semibold">WHY {num}:</span> {breakdown.root_cause_analysis[`why_${num}`]}
                        </p>
                      )
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Root Cause</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.root_cause || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Solution</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.solution || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Temporary Countermeasure</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.temporary_countermeasure || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Permanent Countermeasure</Label>
                    <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.permanent_countermeasure || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Monitoring Actions</Label>
                  <p className="text-sm bg-slate-50 p-2 rounded">{breakdown.root_cause_analysis.monitoring_actions || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Driver Questionnaire */}
          {breakdown.driver_questionnaire && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Driver Questionnaire</h3>
              <div className="space-y-3">
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Did daily inspection?</Label>
                  <p className={breakdown.driver_questionnaire.did_daily_inspection ? 'text-emerald-600' : 'text-red-600'}>
                    {breakdown.driver_questionnaire.did_daily_inspection ? 'Yes' : 'No'}
                  </p>
                  {!breakdown.driver_questionnaire.did_daily_inspection && breakdown.driver_questionnaire.daily_inspection_reason_no && (
                    <p className="text-sm text-slate-500 mt-1">Reason: {breakdown.driver_questionnaire.daily_inspection_reason_no}</p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Reported problems before leaving?</Label>
                  <p className={breakdown.driver_questionnaire.reported_problems_before_leaving ? 'text-emerald-600' : 'text-red-600'}>
                    {breakdown.driver_questionnaire.reported_problems_before_leaving ? 'Yes' : 'No'}
                  </p>
                  {breakdown.driver_questionnaire.reported_problems_reason && (
                    <p className="text-sm text-slate-500 mt-1">{breakdown.driver_questionnaire.reported_problems_reason}</p>
                  )}
                </div>
                <div className="p-3 border rounded">
                  <Label className="text-xs text-slate-500">Problem occurred previously?</Label>
                  <p className={breakdown.driver_questionnaire.problem_occurred_previously ? 'text-amber-600' : 'text-slate-600'}>
                    {breakdown.driver_questionnaire.problem_occurred_previously ? 'Yes' : 'No'}
                  </p>
                  {breakdown.driver_questionnaire.problem_occurred_previously && breakdown.driver_questionnaire.problem_occurred_explanation && (
                    <p className="text-sm text-slate-500 mt-1">{breakdown.driver_questionnaire.problem_occurred_explanation}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Preventability */}
          {breakdown.preventability && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Preventability</h3>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className={`inline-flex items-center px-2.5 py-1 rounded font-medium ${
                  breakdown.preventability === 'preventable' ? 'bg-red-100 text-red-800' :
                  breakdown.preventability === 'not_preventable' ? 'bg-emerald-100 text-emerald-800' :
                  'bg-slate-200 text-slate-700'
                }`}>
                  {breakdown.preventability === 'preventable' ? 'Preventable' :
                   breakdown.preventability === 'not_preventable' ? 'Not Preventable' : 'Undetermined'}
                </span>
                {breakdown.preventability_reason && (
                  <span className="text-slate-700 flex-1 bg-slate-50 px-3 py-1 rounded">{breakdown.preventability_reason}</span>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {breakdown.notes && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Additional Notes</h3>
              <p className="text-sm bg-slate-50 p-3 rounded">{breakdown.notes}</p>
            </div>
          )}

          {/* Submission Info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-100 rounded text-sm">
            <div>
              <span className="text-slate-500">Created by:</span>
              <span className="ml-2">{breakdown.created_by_name}</span>
            </div>
            <div>
              <span className="text-slate-500">Submitted by:</span>
              <span className="ml-2">{breakdown.submitted_by_name || 'Not submitted'}</span>
            </div>
            {breakdown.approved_by_name && (
              <div>
                <span className="text-slate-500">Approved by:</span>
                <span className="ml-2">{breakdown.approved_by_name}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canApprove && breakdown.status === 'pending_approval' && (
            <>
              <Button
                variant="destructive"
                onClick={() => {
                  const reason = prompt('Reason for returning:');
                  if (reason) {
                    onReturn(breakdown, reason);
                    onClose();
                  }
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Return
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  onApprove(breakdown);
                  onClose();
                }}
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Breakdown Form Dialog
const BreakdownFormDialog = ({ open, onClose, breakdown, onSave, vehicles, trailers, drivers, onRefresh }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savedBreakdownId, setSavedBreakdownId] = useState(breakdown?.id || null);
  const [formData, setFormData] = useState({
    date_of_breakdown: new Date().toISOString().slice(0, 16),
    truck_id: '',
    trailer_id: '',
    driver_id: '',
    supporting_docs: {
      driver_statement: { uploaded: false, filename: '', reason: '' },
      maintenance_history: { uploaded: false, filename: '', reason: '' },
      last_service: { uploaded: false, filename: '', reason: '' },
      last_inspection_sheet: { uploaded: false, filename: '', reason: '' },
      workshop_findings: { uploaded: false, filename: '', reason: '' },
    },
    conditions: {
      day_night: '',
      weather: '',
      weather_extreme_details: '',
      time_of_event: '',
      day_of_week: '',
    },
    company_rep_present: { answer: null, reason: '' },
    driver_reported_immediately: { answer: null, reason: '', additional_info: '' },
    root_cause_analysis: {
      problem_description: '',
      why_1: '', why_2: '', why_3: '', why_4: '', why_5: '', why_6: '',
      root_cause: '',
      solution: '',
      temporary_countermeasure: '',
      permanent_countermeasure: '',
      monitoring_actions: '',
    },
    driver_questionnaire: {
      did_daily_inspection: null,
      daily_inspection_reason_no: '',
      reported_problems_before_leaving: null,
      reported_problems_reason: '',
      problem_occurred_previously: null,
      problem_occurred_explanation: '',
    },
    preventability: '',
    preventability_reason: '',
    notes: '',
  });

  useEffect(() => {
    if (breakdown) {
      setSavedBreakdownId(breakdown.id);
      // Convert old format to new format if needed
      const existingDocs = breakdown.supporting_docs || {};
      const convertedDocs = {};
      Object.keys(SUPPORTING_DOC_TYPES).forEach(key => {
        if (typeof existingDocs[key] === 'boolean') {
          convertedDocs[key] = { uploaded: existingDocs[key], filename: '', reason: '' };
        } else {
          convertedDocs[key] = existingDocs[key] || { uploaded: false, filename: '', reason: '' };
        }
      });
      
      setFormData({
        date_of_breakdown: breakdown.date_of_breakdown?.slice(0, 16) || new Date().toISOString().slice(0, 16),
        truck_id: breakdown.truck_id || '',
        trailer_id: breakdown.trailer_id || '',
        driver_id: breakdown.driver_id || '',
        supporting_docs: convertedDocs,
        conditions: breakdown.conditions || formData.conditions,
        company_rep_present: breakdown.company_rep_present || { answer: null, reason: '' },
        driver_reported_immediately: breakdown.driver_reported_immediately || { answer: null, reason: '', additional_info: '' },
        root_cause_analysis: breakdown.root_cause_analysis || formData.root_cause_analysis,
        driver_questionnaire: breakdown.driver_questionnaire || formData.driver_questionnaire,
        preventability: breakdown.preventability || '',
        preventability_reason: breakdown.preventability_reason || '',
        notes: breakdown.notes || '',
      });
    } else {
      setSavedBreakdownId(null);
      setFormData({
        date_of_breakdown: new Date().toISOString().slice(0, 16),
        truck_id: '',
        trailer_id: '',
        driver_id: '',
        supporting_docs: {
          driver_statement: { uploaded: false, filename: '', reason: '' },
          maintenance_history: { uploaded: false, filename: '', reason: '' },
          last_service: { uploaded: false, filename: '', reason: '' },
          last_inspection_sheet: { uploaded: false, filename: '', reason: '' },
          workshop_findings: { uploaded: false, filename: '', reason: '' },
        },
        conditions: {
          day_night: '',
          weather: '',
          weather_extreme_details: '',
          time_of_event: '',
          day_of_week: '',
        },
        company_rep_present: { answer: null, reason: '' },
        driver_reported_immediately: { answer: null, reason: '', additional_info: '' },
        root_cause_analysis: {
          problem_description: '',
          why_1: '', why_2: '', why_3: '', why_4: '', why_5: '', why_6: '',
          root_cause: '',
          solution: '',
          temporary_countermeasure: '',
          permanent_countermeasure: '',
          monitoring_actions: '',
        },
        driver_questionnaire: {
          did_daily_inspection: null,
          daily_inspection_reason_no: '',
          reported_problems_before_leaving: null,
          reported_problems_reason: '',
          problem_occurred_previously: null,
          problem_occurred_explanation: '',
        },
        preventability: '',
        preventability_reason: '',
        notes: '',
      });
    }
  }, [breakdown, open]);

  const updateField = useCallback((section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleDocUpload = useCallback((docKey, filename) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { uploaded: true, filename, reason: '' }
      }
    }));
  }, []);

  const handleDocReasonChange = useCallback((docKey, reason) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { ...prev.supporting_docs[docKey], reason }
      }
    }));
  }, []);

  const validateForm = () => {
    if (!formData.truck_id || !formData.driver_id) {
      toast.error('Please select a truck and driver');
      return false;
    }
    
    // Check that each non-uploaded document has a reason
    for (const [key, doc] of Object.entries(formData.supporting_docs)) {
      if (!doc.uploaded && !doc.reason?.trim()) {
        toast.error(`Please provide a reason for not uploading: ${SUPPORTING_DOC_TYPES[key]}`);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedBreakdownId(result.id);
      }
      onClose();
    } catch (error) {
      console.error('Error saving breakdown:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.truck_id || !formData.driver_id) {
      toast.error('Please select a truck and driver');
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedBreakdownId(result.id);
        toast.success('Draft saved. You can now upload documents.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setLoading(false);
    }
  };

  const trucks = vehicles.filter(v => v.vehicle_type === 'Truck' || v.vehicle_type === 'truck');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {breakdown ? 'Edit Breakdown Report' : 'New Breakdown Report'}
          </DialogTitle>
          <DialogDescription>
            Complete all sections to document the breakdown incident. Upload supporting documents or provide reasons if unavailable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date of Breakdown *</Label>
                <Input
                  type="datetime-local"
                  value={formData.date_of_breakdown}
                  onChange={(e) => updateField(null, 'date_of_breakdown', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Truck Registration *</Label>
                <Select value={formData.truck_id} onValueChange={(v) => updateField(null, 'truck_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select truck" />
                  </SelectTrigger>
                  <SelectContent>
                    {trucks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.registration_number} - {t.make} {t.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Trailer Registration</Label>
                <Select value={formData.trailer_id || 'none'} onValueChange={(v) => updateField(null, 'trailer_id', v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trailer (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Trailer</SelectItem>
                    {trailers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.registration_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Driver Name *</Label>
                <Select value={formData.driver_id} onValueChange={(v) => updateField(null, 'driver_id', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.first_name || d.user?.first_name || ''} {d.last_name || d.user?.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Supporting Documents */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-900">Supporting Documents</h3>
              {!savedBreakdownId && (
                <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Draft to Enable Uploads
                </Button>
              )}
            </div>
            {!savedBreakdownId && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Save the breakdown as a draft first to enable document uploads.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => (
                <SupportingDocUpload
                  key={key}
                  docKey={key}
                  label={label}
                  docData={formData.supporting_docs[key]}
                  onUpload={handleDocUpload}
                  onReasonChange={handleDocReasonChange}
                  breakdownId={savedBreakdownId}
                  disabled={!savedBreakdownId}
                />
              ))}
            </div>
          </div>

          {/* Conditions of Event */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Conditions of Event</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Day/Night</Label>
                <Select value={formData.conditions.day_night || ''} onValueChange={(v) => updateField('conditions', 'day_night', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="night">Night</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Weather</Label>
                <Select value={formData.conditions.weather || ''} onValueChange={(v) => updateField('conditions', 'weather', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sun">Sun</SelectItem>
                    <SelectItem value="rain">Rain</SelectItem>
                    <SelectItem value="cloudy">Cloudy</SelectItem>
                    <SelectItem value="fog">Fog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.conditions.weather === 'rain' && (
                <div className="space-y-2">
                  <Label>If Rain - Extreme Details</Label>
                  <Input
                    value={formData.conditions.weather_extreme_details || ''}
                    onChange={(e) => updateField('conditions', 'weather_extreme_details', e.target.value)}
                    placeholder="Specify extreme conditions"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Time of Event</Label>
                <Input
                  type="time"
                  value={formData.conditions.time_of_event || ''}
                  onChange={(e) => updateField('conditions', 'time_of_event', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select value={formData.conditions.day_of_week || ''} onValueChange={(v) => updateField('conditions', 'day_of_week', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Company Representative */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Company Representative & Reporting</h3>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Was a Company representative at the place of breakdown?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.company_rep_present.answer === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('company_rep_present', 'answer', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.company_rep_present.answer === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('company_rep_present', 'answer', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.company_rep_present.answer === false && (
                  <Input
                    className="mt-2"
                    value={formData.company_rep_present.reason || ''}
                    onChange={(e) => updateField('company_rep_present', 'reason', e.target.value)}
                    placeholder="Reason for no"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Did the driver report the breakdown to Manager/Supervisor immediately?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_reported_immediately.answer === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_reported_immediately', 'answer', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_reported_immediately.answer === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_reported_immediately', 'answer', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_reported_immediately.answer === false && (
                  <div className="mt-2 space-y-2">
                    <Input
                      value={formData.driver_reported_immediately.reason || ''}
                      onChange={(e) => updateField('driver_reported_immediately', 'reason', e.target.value)}
                      placeholder="Reason for no"
                    />
                    <Input
                      value={formData.driver_reported_immediately.additional_info || ''}
                      onChange={(e) => updateField('driver_reported_immediately', 'additional_info', e.target.value)}
                      placeholder="When was it reported?"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Root Cause Analysis */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Root Cause Analysis</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>What was the problem?</Label>
                <Textarea
                  value={formData.root_cause_analysis.problem_description || ''}
                  onChange={(e) => updateField('root_cause_analysis', 'problem_description', e.target.value)}
                  placeholder="Describe the problem"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">6 WHY's Analysis</Label>
                <div className="grid gap-2">
                  {[1, 2, 3, 4, 5, 6].map(num => (
                    <div key={num} className="flex items-center gap-2">
                      <Label className="w-16 text-sm">WHY {num}:</Label>
                      <Input
                        value={formData.root_cause_analysis[`why_${num}`] || ''}
                        onChange={(e) => updateField('root_cause_analysis', `why_${num}`, e.target.value)}
                        placeholder={`Why ${num}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Root Cause</Label>
                  <Textarea
                    value={formData.root_cause_analysis.root_cause || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'root_cause', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Solution</Label>
                  <Textarea
                    value={formData.root_cause_analysis.solution || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'solution', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Temporary Countermeasure</Label>
                  <Textarea
                    value={formData.root_cause_analysis.temporary_countermeasure || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'temporary_countermeasure', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Permanent Countermeasure</Label>
                  <Textarea
                    value={formData.root_cause_analysis.permanent_countermeasure || ''}
                    onChange={(e) => updateField('root_cause_analysis', 'permanent_countermeasure', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Monitoring Actions</Label>
                <Textarea
                  value={formData.root_cause_analysis.monitoring_actions || ''}
                  onChange={(e) => updateField('root_cause_analysis', 'monitoring_actions', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Driver Questionnaire */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Driver Questionnaire</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Did you do your daily inspection on the truck prior to leaving the yard?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.did_daily_inspection === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'did_daily_inspection', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.did_daily_inspection === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'did_daily_inspection', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_questionnaire.did_daily_inspection === false && (
                  <Input
                    className="mt-2"
                    value={formData.driver_questionnaire.daily_inspection_reason_no || ''}
                    onChange={(e) => updateField('driver_questionnaire', 'daily_inspection_reason_no', e.target.value)}
                    placeholder="Reason for no"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Did you report any problems to your Manager before leaving?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.reported_problems_before_leaving === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'reported_problems_before_leaving', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.reported_problems_before_leaving === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'reported_problems_before_leaving', false)}
                  >
                    No
                  </Button>
                </div>
                <Input
                  className="mt-2"
                  value={formData.driver_questionnaire.reported_problems_reason || ''}
                  onChange={(e) => updateField('driver_questionnaire', 'reported_problems_reason', e.target.value)}
                  placeholder={formData.driver_questionnaire.reported_problems_before_leaving ? "What was reported?" : "Reason for no"}
                />
              </div>
              <div className="space-y-2">
                <Label>Has this problem occurred previously on your truck?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.problem_occurred_previously === true ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'problem_occurred_previously', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.driver_questionnaire.problem_occurred_previously === false ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateField('driver_questionnaire', 'problem_occurred_previously', false)}
                  >
                    No
                  </Button>
                </div>
                {formData.driver_questionnaire.problem_occurred_previously === true && (
                  <Textarea
                    className="mt-2"
                    value={formData.driver_questionnaire.problem_occurred_explanation || ''}
                    onChange={(e) => updateField('driver_questionnaire', 'problem_occurred_explanation', e.target.value)}
                    placeholder="Explain previous occurrence"
                    rows={2}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Preventability */}
          <div className="space-y-2 p-4 bg-slate-50 rounded-lg border">
            <Label className="text-sm font-semibold text-slate-800">Preventability Assessment</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: 'preventable', label: 'Preventable', color: 'text-red-700 border-red-300 bg-red-50' },
                { value: 'not_preventable', label: 'Not Preventable', color: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
                { value: 'undetermined', label: 'Undetermined', color: 'text-slate-700 border-slate-300 bg-white' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateField(null, 'preventability', opt.value)}
                  className={`px-3 py-2 rounded border text-sm font-medium transition-all ${formData.preventability === opt.value ? `${opt.color} ring-2 ring-offset-1 ring-slate-400` : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`breakdown-preventability-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Textarea
              value={formData.preventability_reason || ''}
              onChange={(e) => updateField(null, 'preventability_reason', e.target.value)}
              placeholder="Reason / justification for the preventability decision..."
              rows={2}
              data-testid="breakdown-preventability-reason"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={formData.notes || ''}
              onChange={(e) => updateField(null, 'notes', e.target.value)}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {breakdown ? 'Update' : 'Create'} Breakdown
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const BreakdownsPage = () => {
  const { user, isAdmin, isSupervisor, isManagement } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  const [breakdowns, setBreakdowns] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [breakdownsRes, pendingRes, statsRes, vehiclesRes, trailersRes, driversRes] = await Promise.all([
        breakdownsAPI.list(),
        breakdownsAPI.getPendingApproval(),
        breakdownsAPI.getStatistics(),
        vehiclesAPI.list(),
        trailersAPI.list(),
        driversAPI.list(),
      ]);
      
      setBreakdowns(breakdownsRes.data || []);
      setPendingApproval(pendingRes.data || []);
      setStatistics(statsRes.data || {});
      setVehicles(vehiclesRes.data || []);
      setTrailers(trailersRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load breakdowns data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateBreakdown = async (data) => {
    try {
      let result;
      if (selectedBreakdown) {
        result = await breakdownsAPI.update(selectedBreakdown.id, data);
        toast.success('Breakdown updated successfully');
      } else {
        result = await breakdownsAPI.create(data, user.id, `${user.first_name} ${user.last_name}`);
        toast.success('Breakdown created successfully');
      }
      setSelectedBreakdown(null);
      fetchData();
      return result.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save breakdown');
      throw error;
    }
  };

  const handleSubmit = async (breakdown) => {
    try {
      await breakdownsAPI.submit(breakdown.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Breakdown submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit breakdown');
    }
  };

  const handleApprove = async (breakdown) => {
    try {
      await breakdownsAPI.approve(breakdown.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Breakdown approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve breakdown');
    }
  };

  const handleReturn = async (breakdown, reason) => {
    try {
      await breakdownsAPI.return(breakdown.id, reason, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Breakdown returned for corrections');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return breakdown');
    }
  };

  const handleDelete = async (breakdown) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    try {
      await breakdownsAPI.delete(breakdown.id);
      toast.success('Breakdown deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete breakdown');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const canApprove = isManagement || isAdmin;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="breakdowns-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            BREAKDOWNS
          </h1>
          <p className="text-slate-500 mt-1">
            Manage and track vehicle breakdown reports
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={() => breakdownsAPI.exportExcel()}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button 
            onClick={() => {
              setSelectedBreakdown(null);
              setFormDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            New Breakdown
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Open</p>
                  <p className="text-2xl font-bold">{statistics.open_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-2xl font-bold">{statistics.pending_approval_count}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Closed</p>
                  <p className="text-2xl font-bold">{statistics.closed_count}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Month</p>
                  <p className="text-2xl font-bold">{statistics.month_count}</p>
                </div>
                <Calendar className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Breakdowns ({breakdowns.length})</TabsTrigger>
          {canApprove && (
            <TabsTrigger value="pending">
              Pending Approval ({pendingApproval.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {breakdowns.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No breakdowns recorded</h3>
                <p className="text-slate-500 mt-1 mb-4">Create a new breakdown report to get started</p>
                <Button onClick={() => setFormDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Breakdown
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {breakdowns.map((breakdown) => (
                <Card 
                  key={breakdown.id}
                  className="hover:shadow-md transition-all cursor-pointer"
                  data-testid={`breakdown-card-${breakdown.id}`}
                  onClick={() => {
                    setSelectedBreakdown(breakdown);
                    setDetailDialogOpen(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold">{breakdown.truck_registration}</span>
                            {breakdown.trailer_registration && (
                              <>
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                                <span className="font-mono text-slate-600">{breakdown.trailer_registration}</span>
                              </>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {breakdown.date_of_breakdown?.slice(0, 10)}
                            </span>
                            <span>
                              <User className="w-3 h-3 inline mr-1" />
                              {breakdown.driver_name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusBadge(breakdown.status)}>
                          {getStatusLabel(breakdown.status)}
                        </Badge>
                        {breakdown.status === 'draft' && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBreakdown(breakdown);
                                setFormDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubmit(breakdown);
                              }}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(breakdown);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {breakdown.status === 'pending_approval' && canApprove && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(breakdown);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                const reason = prompt('Reason for returning:');
                                if (reason) handleReturn(breakdown, reason);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          {pendingApproval.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No pending approvals</h3>
                <p className="text-slate-500 mt-1">All breakdown reports have been reviewed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingApproval.map((breakdown) => (
                <Card key={breakdown.id} className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold">{breakdown.truck_registration}</span>
                          <span className="text-sm text-slate-500">
                            - {breakdown.date_of_breakdown?.slice(0, 10)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Submitted by: {breakdown.submitted_by_name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedBreakdown(breakdown);
                            setDetailDialogOpen(true);
                          }}
                          data-testid={`review-btn-${breakdown.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(breakdown)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const reason = prompt('Reason for returning:');
                            if (reason) handleReturn(breakdown, reason);
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Return
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <BreakdownFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedBreakdown(null);
        }}
        breakdown={selectedBreakdown}
        onSave={handleCreateBreakdown}
        vehicles={vehicles}
        trailers={trailers}
        drivers={drivers}
        onRefresh={fetchData}
      />

      {/* Detail Dialog */}
      <BreakdownDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedBreakdown(null);
        }}
        breakdown={selectedBreakdown}
        onApprove={handleApprove}
        onReturn={handleReturn}
        canApprove={canApprove}
      />
    </div>
  );
};

export default BreakdownsPage;
